import React, { useRef, useEffect, useState, useCallback } from 'react';
import Mindmap from "@/components/MindMap";
import { Button } from "@/components/ui/button";
import { useTheme } from "next-themes";
import { Brain, Sparkles, GitBranch, FolderOpen, Code2, Rocket, Plus, MousePointerClick } from 'lucide-react';
import { MindmapFloatingActionButtons } from './MindmapFloatingActionButtons';
import { EmptyState } from '../ui/interactive-empty-state';
import { SpinnerIcon } from '../ui/SpinnerIcon';

interface MindMapSectionProps {
  mindmapData: string;
  isLoading: boolean;
  onGenerateMindmap: () => void | Promise<void>;
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void;
  episodeId?: string;
}


export const MindMapSection = React.memo(
  ({
    mindmapData,
    isLoading,
    onGenerateMindmap,
    onTimestampClick,
    episodeId,
  }: MindMapSectionProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark';

    // Translation state
    const [showTranslation, setShowTranslation] = useState(false);
    const [translationData, setTranslationData] = useState<string | null>(null);
    const [isGeneratingTranslation, setIsGeneratingTranslation] = useState(false);
    
    // Mindmap generation state
    const [isGeneratingMindmap, setIsGeneratingMindmap] = useState(false);

    useEffect(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }, [mindmapData]);

    // Translation functionality
    const handleToggleTranslation = useCallback(async () => {
      console.log('Toggle mindmap translation called with episodeId:', episodeId);
      
      const newShowTranslation = !showTranslation;
      setShowTranslation(newShowTranslation);

      // If hiding translation, just return
      if (!newShowTranslation) {
        return;
      }

      // If showing translation and we don't have data yet, generate it
      if (newShowTranslation && !translationData) {
        if (!episodeId) {
          console.error('Episode ID is required for mindmap translation');
          return;
        }
        
        console.log('Generating mindmap translation for episode:', episodeId);
        setIsGeneratingTranslation(true);
        
        try {
          const response = await fetch('/api/translate-mindmap-gemini', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ episodeId }),
          });

          if (response.ok) {
            const result = await response.json();
            setTranslationData(result.translation);
            console.log('Mindmap translation generated successfully');
          } else {
            const error = await response.json();
            console.error('Mindmap translation API error:', error);
            // Show error to user but keep translation panel open
            setTranslationData(null);
          }
        } catch (error) {
          console.error('Error generating mindmap translation:', error);
          setTranslationData(null);
        } finally {
          setIsGeneratingTranslation(false);
        }
      }
    }, [showTranslation, translationData, episodeId]);

    // Handle mindmap generation
    const handleGenerateMindmap = useCallback(() => {
      setIsGeneratingMindmap(true);
      // Call onGenerateMindmap and handle the promise if it returns one
      const result = onGenerateMindmap();
      if (result && typeof result.then === 'function') {
        result.finally(() => {
          setIsGeneratingMindmap(false);
        });
      } else {
        // If it's not a promise, reset the state after a short delay
        setTimeout(() => {
          setIsGeneratingMindmap(false);
        }, 100);
      }
    }, [onGenerateMindmap]);

    if (!mindmapData) {
      return (
        <div className="flex flex-col w-full bg-background/30 backdrop-blur-sm rounded-lg border border-border/50">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 p-4 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-6 bg-primary rounded-full"></div>
              <h3 className="text-xl font-bold text-foreground">Mind Map</h3>
            </div>
          </div>
          <EmptyState
              theme={theme}
              title={isGeneratingMindmap ? "Generating Mind Map..." : "No Mind Map Available"}
              description={isGeneratingMindmap ? "Please wait while we create your interactive mind map." : "Generate an interactive mind map to visualize the structure and key concepts of this episode."}
              icons={[<GitBranch key="p1" className="h-6 w-6" />, <Brain key="p2" className="h-6 w-6" />, <Sparkles key="p3" className="h-6 w-6" />]}
              action={{
                label: isGeneratingMindmap ? "Generating..." : "Generate Mindmap",
                icon: isGeneratingMindmap ? <SpinnerIcon size="sm" /> : <MousePointerClick className="h-4 w-4" />,
                onClick: handleGenerateMindmap,
                disabled: isGeneratingMindmap
              }}
            />
          {/* Empty State */}
          {/* <div className="flex flex-col items-center justify-center w-full h-96 space-y-6 p-8">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mb-4">
                <GitBranch className="h-10 w-10 text-purple-600" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                <Sparkles className="h-3 w-3 text-white" />
              </div>
            </div>
            
            <div className="text-center space-y-3 max-w-md">
              <h4 className="text-lg font-semibold text-foreground">No mind map available</h4>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Generate an interactive mind map to visualize the structure and key concepts of this episode
              </p>
            </div>
            
            <Button
              onClick={onGenerateMindmap}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2.5 text-sm font-medium"
            >
              <Brain className="h-4 w-4 mr-2" />
              Generate Mind Map
            </Button>
          </div> */}
        </div>
      );
    }

    return (
      <>
        {/* Floating Action Buttons */}
        {mindmapData && (
          <MindmapFloatingActionButtons
            showTranslation={showTranslation}
            onToggleTranslation={handleToggleTranslation}
            isGeneratingTranslation={isGeneratingTranslation}
          />
        )}

        <div className="flex flex-col w-full bg-background/30 backdrop-blur-sm rounded-lg border border-border/50">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 p-4 border-b border-border/50">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-6 bg-primary rounded-full"></div>
              <h3 className="text-xl font-bold text-foreground">
                {showTranslation ? 'Mind Map - 中文翻译' : 'Mind Map'}
              </h3>
            </div>
            <div className="flex items-center space-x-2">
              <GitBranch className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Interactive visualization</span>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 p-4">
            <div ref={contentRef} className="h-full w-full">
              {isGeneratingTranslation ? (
                <div className="flex justify-center items-center h-96">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-[2.5px] border-primary"></div>
                  <span className="ml-3">Generating translation...</span>
                </div>
              ) : (
                <Mindmap
                  markdown={showTranslation && translationData ? translationData : mindmapData}
                  onTimestampClick={onTimestampClick}
                  darkMode={isDarkMode}
                />
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
);

MindMapSection.displayName = "MindMapSection";
